import React, { useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import ListItemText from '@mui/material/ListItemText'
import { FixedSizeList } from 'react-window'
import { TagStyled } from './styled'

type IItemOption = {
  value: string | number
  label: string
}

interface IsSelectProps {
  label: string
  error?: string | undefined
  value: Array<string | number> | null
  onChange?: (value: Array<string | number>) => void
  width?: string
  maxWidth?: string
  options: IItemOption[]
  style?: React.CSSProperties
  onBlur?: () => void
  disabled?: boolean
  limitTags?: number
  noChip?: boolean
  showCheckboxes?: boolean
}

const ListboxComponent = React.forwardRef<HTMLDivElement, any>(function ListboxComponent({ children, ...other }, ref) {
  const height = children.length > 10 ? 400 : children.length > 5 ? 300 : 250
  return (
    <div ref={ref} {...other} style={{ overflow: 'auto', maxHeight: 400 }}>
      <FixedSizeList height={height} itemCount={React.Children.count(children)} itemSize={55} width="100%">
        {({ index, style }) => {
          const child = React.Children.toArray(children)[index]
          if (!React.isValidElement(child)) return null
          return (
            <div style={style} key={child.key}>
              {child}
            </div>
          )
        }}
      </FixedSizeList>
    </div>
  )
})

export default function MultiSelect(props: IsSelectProps) {
  const {
    label,
    error,
    value = [],
    options = [],
    onChange,
    onBlur,
    width,
    disabled,
    limitTags,
    noChip,
    maxWidth,
    showCheckboxes = true,
  } = props

  const [inputValue, setInputValue] = useState<string>('')

  const handleChange = (event: any, newValue: Array<string | number> | IItemOption[]) => {
    if (onChange) {
      const selectedValues: Array<string | number> = newValue
        ? Array.isArray(newValue) && typeof newValue[0] === 'object'
          ? (newValue as IItemOption[]).map((option) => option.value) // Mapeamos a los valores
          : (newValue as Array<string | number>) // Ya es un array de string | number
        : []
      onChange(selectedValues)
      setInputValue('')
    }
  }

  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))

  const renderOption = (
    renderProps: any,
    option: IItemOption,
    { selected }: { selected: boolean }
  ): React.ReactNode => {
    return (
      <div
        {...renderProps}
        key={`list-select-${option.label}-${option.value}`}
        style={{ display: 'flex', alignItems: 'center', padding: '20px 0 20px 0' }}
      >
        {showCheckboxes && (
          <Checkbox
            onChange={() => {
              const newSelectedValues = selected
                ? (value || []).filter((val) => val !== option.value)
                : [...(value || []), option.value]
              handleChange(null, newSelectedValues)
            }}
            checked={selected}
          />
        )}
        <ListItemText primary={option.label} />
      </div>
    )
  }

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      disabled={disabled}
      id="multi-select"
      options={filteredOptions}
      value={options.filter((option) => (Array.isArray(value) ? value.includes(option.value) : false))}
      getOptionLabel={(option) => option.label}
      onChange={handleChange}
      onBlur={onBlur}
      limitTags={limitTags ? limitTags : undefined}
      inputValue={inputValue}
      renderTags={
        noChip
          ? (tag) => {
              return (
                <TagStyled>
                  {tag
                    .filter((option) => option.value !== 0)
                    .map((option, index) => (
                      <div key={`multi-select-index-${index}`}>
                        {index === tag.length - 1 ? option.label : `${option.label}, `}
                      </div>
                    ))}
                </TagStyled>
              )
            }
          : undefined
      }
      renderOption={(properties, option, { selected }) => {
        return renderOption(properties, option, { selected })
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder="Buscar"
          error={!!error}
          helperText={error}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          sx={{
            '.MuiFormHelperText-root': {
              position: 'absolute',
              bottom: -24,
              borderRadius: '16px',
            },
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
          }}
        />
      )}
      ListboxComponent={ListboxComponent}
      sx={{
        backgroundColor: '#FFF',
        width: width || '100%',
        minWidth: '240px',
        maxWidth: maxWidth || '300px',
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
        '.MuiOutlinedInput-notchedOutline': { border: 0 },
        borderRadius: '16px',
      }}
    />
  )
}
